import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium font-headline transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary-950 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 dark:focus-visible:ring-primary-300",
  {
    variants: {
      variant: {
        default:
          "bg-primary-600 text-white shadow hover:bg-primary-700 dark:bg-primary-600 dark:text-white dark:hover:bg-primary-700",
        accent:
          "bg-accent-500 text-white shadow hover:bg-accent-600 dark:bg-accent-600 dark:text-white dark:hover:bg-accent-700",
        success:
          "bg-secondary-600 text-white shadow hover:bg-secondary-700 dark:bg-secondary-600 dark:text-white dark:hover:bg-secondary-700",
        destructive:
          "bg-red-500 text-zinc-50 shadow-sm hover:bg-red-500/90 dark:bg-red-900 dark:text-zinc-50 dark:hover:bg-red-900/90",
        outline:
          "border border-primary-200 bg-white shadow-sm hover:bg-primary-50 hover:text-primary-900 dark:border-primary-800 dark:bg-zinc-950 dark:hover:bg-primary-800 dark:hover:text-zinc-50",
        "outline-accent":
          "border border-accent-200 bg-white text-accent-700 shadow-sm hover:bg-accent-50 hover:text-accent-800 dark:border-accent-800 dark:bg-zinc-950 dark:text-accent-400 dark:hover:bg-accent-900 dark:hover:text-accent-300",
        secondary:
          "bg-primary-100 text-primary-900 shadow-sm hover:bg-primary-200 dark:bg-primary-800 dark:text-zinc-50 dark:hover:bg-primary-700",
        ghost: "hover:bg-primary-100 hover:text-primary-900 dark:hover:bg-primary-800 dark:hover:text-zinc-50",
        link: "text-primary-600 underline-offset-4 hover:underline dark:text-primary-400",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
